import type { NavItem, NavNotice, SidebarSection } from '@forecastify/ui';
import { DEALS_ENABLED, REPORTS_FLAG, type SidebarView } from '@forecastify/shared';
import type { Translate } from '../../i18n';
import { ROUTES, type RouteEntry } from '../../routes';
import { withoutWorkspacePrefix } from '../../workspace-path';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ The sidebar's rows, from the route table and the sidebar read.            │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **OUT OF `Chrome`, SO THE SETUP FLOW'S PREVIEW DRAWS THE SAME SIDEBAR** — `0202`, and the
 * product owner's words: *"make sure this preview's sidebar matches what's exactly in the app."*
 * The mockup drew a menu of its own, which could not know what the app contains. A second builder
 * in the preview would be the same mistake one file over: two lists that agree on the day they are
 * written. So both callers hand this the same three things — the translator, a sidebar read and
 * how to make an address — and the preview's read is a new workspace's, built by hand.
 *
 * Everything below moved verbatim from `app.tsx`, comments included.
 */
export interface SidebarRowsInput {
  readonly t: Translate;
  readonly view: SidebarView;
  /** The concrete address in the bar, for `active`. */
  readonly pathname: string;
  /** Workspace-relative path → concrete address. */
  readonly here: (path: string) => string;
  /** Workspace-relative path → a click handler that goes there. */
  readonly go: (path: string) => () => void;
  /**
   * 22 Oct 2026 · whether the reader holds an action — a section's `+` is drawn only for somebody
   * who may create what it creates. Absent (the setup preview) means everything is allowed.
   */
  readonly may?: (action: string) => boolean;
  /**
   * 28 Oct 2026 · marks for a row, keyed by its workspace-relative path like `countFor` — the *Idő*
   * row's own logging issues. Absent (the setup preview) means none.
   */
  readonly notices?: Readonly<Record<string, readonly NavNotice[]>>;
}

/**
 * The routes with a sidebar entrance that this reader is entitled to. Also ⌘K's list.
 *
 * ⚠️ Filtered HERE rather than where the rows are drawn, so ⌘K contains the same list: a palette
 * entry for a screen the sidebar hides is the stale entry that looks like a working one until it
 * is pressed.
 */
export function sidebarRoutes(sidebarView: SidebarView): RouteEntry[] {
  /**
   * ⚠️⚠️ **THE SAME SHAPE AND THE SAME DEFAULT AS `featureAllows`, AND THE DEFAULT WAS THE BUG.**
   * The first version started both entitlements at false — *not answered* read as *no* — so both
   * rows appeared one paint after the sidebar drew, and the reachability gate failed exactly as it
   * should: a link that is absent on the first frame is not a link anybody can click. Unanswered
   * means the row stays; the read that carries the counts takes it away.
   */
  const entitlementAllows = (route: RouteEntry): boolean =>
    route.entitlement === undefined || sidebarView.entitlements[route.entitlement];

  return ROUTES.filter((route) => route.entrance === 'sidebar' && entitlementAllows(route));
}

export function sidebarRows({
  t,
  view: sidebarView,
  pathname,
  here,
  go,
  may = () => true,
  notices = {},
}: SidebarRowsInput): {
  nav: NavItem[];
  sections: SidebarSection[];
} {
  const counts = sidebarView.counts;
  const sidebar = sidebarRoutes(sidebarView);

  /**
   * ⚠️⚠️ **A ROW WHOSE FEATURE IS OFF IS NOT DRAWN, AND THE ROUTE STILL EXISTS.** `routes.tsx`
   * argues the split: the address must keep resolving or it falls through to the catch-all, which is
   * one of the four route failures CLAUDE.md §4 names. Only the entrance goes.
   *
   * ⚠️ **`=== false`, not falsy.** Before the sidebar read resolves, `features` is `{}` and every
   * flagged row would vanish and then reappear — a nav that rearranges itself one paint after it
   * draws. `undefined` means "not answered yet", and the answer for a shipped feature is on.
   */
  const featureAllows = (route: { readonly feature?: string }): boolean =>
    route.feature === undefined || sidebarView.features[route.feature] !== false;

  /**
   * One row, from the route's own declaration. Nothing here is written twice.
   *
   * ⚠️⚠️ **R-38 · `route.path` IS A PATTERN NOW, NOT AN ADDRESS.** It is `/:workspace/tasks`, so
   * using it as an `href` would put the literal string `:workspace` in the sidebar — a link that
   * navigates to a workspace called `:workspace` and lands on "that address does not lead
   * anywhere". `here()` substitutes the workspace in the address bar, which is the only one this
   * shell is ever rendered for.
   *
   * ⚠️ **`active` compares the CONCRETE address**, for the same reason: `location.pathname` is
   * `/delivery/tasks` and never the pattern, so an equality test against `route.path` would
   * highlight nothing at all.
   */
  const rowFor = (route: RouteEntry) => {
    const relative = withoutWorkspacePrefix(route.path);
    const address = here(relative);
    const marks = notices[relative];
    return {
      ...(marks === undefined || marks.length === 0 ? {} : { notices: marks }),
      label: route.navLabel === undefined ? t(route.title) : t(route.navLabel),
      icon: route.icon ?? ('folder' as const),
      href: address,
      active: pathname === address,
      /* ⚠️ The RELATIVE path — `go` applies the prefix, like every other call in this file. */
      onSelect: go(relative),
    };
  };

  /**
   * ⚠⚠️ **THE COUNTER BESIDE A ROW IS KEYED BY THE ROW'S OWN PATH, NOT BY ITS POSITION.** The
   * mockup hard-codes five numbers into five hard-coded rows; ours are generated from `routes.tsx`,
   * so a lookup by address is the only join that survives somebody reordering the table — which is
   * the whole reason the nav is generated in the first place.
   *
   * ⚠️ **A route with no entry here simply has no number**, which is correct for Settings and My
   * account: there is nothing countable about them, and inventing a zero would be a figure that
   * means nothing.
   */
  /**
   * ⚠️⚠️ **R-38 · KEYED BY THE WORKSPACE-RELATIVE PATH, WHICH IS WHAT THESE LITERALS ALREADY ARE.**
   * The lookup below strips the prefix off `route.path` before asking, so this table did not have
   * to gain `/:workspace` seven times — and it should not, because the number beside "Projects" is
   * a fact about the screen and not about which workspace is open.
   */
  const countFor: Readonly<Record<string, number | null | undefined>> = {
    '/projects': counts.projects,
    '/clients': counts.clients,
    '/people': counts.people,
    '/tasks': counts.tasks,
    '/contracts': counts.contracts,
    '/invoices': counts.invoices,
    /* ⚠️ The Workspace row is `/worklogs`; keyed on `/time/logs` (the personal list, which has no
       sidebar row) the count matched no row and the badge never drew. */
    '/worklogs': counts.worklogs,
    '/reports': counts.reports,
    /* Phase 6 · the open deals within the reader's reach. */
    '/deals': counts.deals,
    /**
     * C47 · what THIS caller may decide, not what is pending — `sidebar.service.ts` says why.
     *
     * ⚠⚠ **ON THE LEAD'S QUEUE, NOT ON THE WORKSPACE LIST.** The number answers *how many are
     * waiting for ME*, which is the question `/to-decide` is; beside `/approvals` it would read as
     * the size of a list it does not describe.
     */
    '/to-decide': counts.pendingApprovals,
    /* ⚠️⚠️ **AND THE GLOBAL LIST COUNTS THE WORKSPACE** — every request, every status, since
       13 Oct: the size of the list, like every other row in the group. `sidebar.ts` says why. */
    '/approvals': counts.requests,
  };

  /** ⚠️ Both personal request screens, in declaration order. See the note on `nav` below. */
  const requestRows = sidebar.filter((route) => route.group === 'requests' && featureAllows(route));

  const nav: NavItem[] = sidebar
    .filter(
      (route) =>
        featureAllows(route) &&
        (route.group === 'primary' ||
          (route.group === 'requests' && (requestRows.length === 1 || route === requestRows[0]))),
    )
    .map((route) =>
      route.group === 'requests' && requestRows.length > 1
        ? {
            label: t('nav.requestsSection'),
            icon: 'inbox' as const,
            /* ⚠️ No `href` and no navigation: the group row opens and closes, and the
                   addresses belong to its children. `app-shell.tsx` argues it. */
            onSelect: () => undefined,
            /* ⚠️ **No icon on the children.** The group row carries the inbox for the whole
                   group; a glyph repeated on each child says nothing the indent has not. */
            items: requestRows.map((one) => ({ ...rowFor(one), icon: undefined })),
          }
        : rowFor(route),
    );

  const workspaceRows = sidebar
    .filter((route) => route.group === 'workspace' && featureAllows(route))
    .map((route) => {
      /* ⚠️ R-38 · the prefix comes off before the lookup — `countFor` is keyed by the
             workspace-relative path, because the number is a fact about the screen. */
      const count = countFor[withoutWorkspacePrefix(route.path)];
      return {
        ...rowFor(route),
        icon: route.icon,
        /* ⚠️ `null` means "you may not see this", and draws no number — `sidebar.ts` argues
         * why that is not the same as `0`. */
        ...(count === null || count === undefined ? {} : { count }),
      };
    });

  const sections: SidebarSection[] = [
    /**
     * ⚠️⚠️ **`Dashboardok` FIRST IN THE SCROLL REGION — THE MOCKUP'S OWN ORDER.** Logolás and
     * Szabadságok are read-only views over everybody's work, so they sit above the pinned
     * projects rather than inside `Workspace` beside the things people edit. Ours had them
     * scattered into that list, which is what the report was about.
     *
     * ⚠️ **It disappears when neither row is entitled**, like the pinned sections below: a
     * heading over nothing reads as a list that failed to load.
     */
    ...(sidebar.filter((route) => route.group === 'dashboards' && featureAllows(route)).length === 0
      ? []
      : [
          {
            label: t('nav.dashboardsSection'),
            entries: sidebar
              .filter((route) => route.group === 'dashboards' && featureAllows(route))
              .map((route) => ({ ...rowFor(route), icon: route.icon })),
          },
        ]),
    /**
     * ⚠⚠️ **PINNED PROJECTS AND REPORTS, WHERE THE MOCKUP LISTS EVERYTHING.** `0030` argues
     * the divergence: `main.jsx` renders every project in the workspace and the first three
     * saved reports in array order, which works at prototype scale and survives no real
     * workspace. The SHAPE here is the mockup's exactly — heading that opens the full list, a
     * `+` that starts a new one, colour dot, name, open-task count.
     *
     * ⚠️⚠️ **PROJEKTEK STAYS WHEN NOTHING IS PINNED — 13 Oct 2026.** Its heading is the entrance
     * to *Projektjeim*, the reader's own list (`routes.tsx`), so hiding the section would hide
     * the screen; a line under the heading says how the list fills instead. It goes only for
     * somebody who may not view projects at all, whose count is `null`.
     *
     * ⚠️ **Riportok still disappears when nothing is pinned.** An empty section is a heading
     * and a `+` floating over nothing, which reads as a list that failed to load; its full list
     * is one click away in the Workspace group.
     */
    ...(counts.projects === null
      ? []
      : [
          {
            label: t('nav.projectsSection'),
            /* The section's one glyph in the icon rail — `SidebarSection.icon`. */
            icon: 'folder' as const,
            onSelectAll: go('/my-projects'),
            allHref: here('/my-projects'),
            allLabel: t('nav.myProjects'),
            emptyHint: t('nav.projectsEmpty'),
            /* ⚠️ `?new=1` rather than a route: creating a project is a DIALOG on the list
             * screen, and the query parameter is what lets the shell ask for it without the
             * shell knowing anything about that screen's internals. */
            ...(may('project.create')
              ? { onAdd: go('/projects?new=1'), addLabel: t('nav.newProject') }
              : {}),
            entries: sidebarView.projects.map((project) => ({
              label: project.name,
              ...(project.color === null ? {} : { color: project.color }),
              count: project.tasks,
              href: here(`/projects/${project.id}`),
              onSelect: go(`/projects/${project.id}`),
            })),
          },
        ]),
    /* Phase 6 · *Dealek*, above Riportok as the mockup draws it: the starred deals with their
       open to-dos, `+` for a new one. Gone without `deal.view`, whose count is `null` — and while
       deal management is off (`DEALS_ENABLED`), whatever the count says. */
    ...(!DEALS_ENABLED || counts.deals === null
      ? []
      : [
          {
            label: t('nav.dealsSection'),
            icon: 'tag' as const,
            onSelectAll: go('/deals'),
            allHref: here('/deals'),
            allLabel: t('nav.allDeals'),
            emptyHint: t('nav.dealsEmpty'),
            ...(may('deal.manage')
              ? { onAdd: go('/deals?new=1'), addLabel: t('nav.newDeal') }
              : {}),
            entries: sidebarView.deals.map((deal) => ({
              label: deal.name,
              ...(deal.color === null ? {} : { color: deal.color }),
              count: deal.todos,
              href: here(`/deals/${deal.identifier}`),
              onSelect: go(`/deals/${deal.identifier}`),
            })),
          },
        ]),
    /* ⚠️ Riportok stays when nothing is pinned too (13 Oct 2026) — its heading is
           *Riportjaim*'s entrance. It goes for a plan without reporting and for somebody without
           `report.view`, whose count is `null`. */
    ...(counts.reports === null || sidebarView.features[REPORTS_FLAG] === false
      ? []
      : [
          {
            label: t('nav.reportsSection'),
            icon: 'chart-column' as const,
            onSelectAll: go('/my-reports'),
            allHref: here('/my-reports'),
            allLabel: t('nav.myReports'),
            emptyHint: t('nav.reportsEmpty'),
            /* The builder is a real address (C15), so this one needs no parameter. */
            ...(may('report.manage')
              ? { onAdd: go('/reports/new'), addLabel: t('nav.newReport') }
              : {}),
            entries: sidebarView.reports.map((report) => ({
              label: report.name,
              icon: 'chart-column' as const,
              href: here(`/reports/${report.id}`),
              onSelect: go(`/reports/${report.id}`),
            })),
          },
        ]),
    /* 22 Oct 2026 · gone, heading and all, when none of its rows is granted (`menu.*`). */
    ...(workspaceRows.length === 0
      ? []
      : [{ label: t('nav.workspaceSection'), entries: workspaceRows }]),
  ];

  return { nav, sections };
}
